import { useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ApiError } from "@/api/client";
import { useProjectOverviewQuery } from "@/api/hooks";
import {
  classifySessionFailure,
  useCreatePreviewSessionMutation,
  usePreviewCandidatesQuery,
  usePreviewPatientsQuery,
} from "@/api/preview";
import { useCreatePreviewManualSessionMutation } from "@/api/previewManual";
import { classifySimRunFailure, type SimRunFailure } from "@/api/previewSim";
import { useCapability } from "@/auth/SessionContext";
import { patientForLanguage, rememberRun } from "@/lib/labRunMemory";
import {
  SIM_LANGUAGES,
  SIM_LANGUAGE_LABELS,
  type SimLanguage,
} from "@/lib/previewSim";
import { Button } from "@/ui/Button";
import { Select } from "@/ui/Input";
import { LabBarField, LabMoreMenu } from "./LabBarField";
import { defaultLabFlags, type LabFlags } from "@/lib/labServices";

import { LabImportConversation } from "./LabImportConversation";
import { LabPatientField } from "./LabPatientField";
import { LabRunOptions } from "./LabRunOptions";

const FAILURE_MESSAGES = {
  stale_target:
    "This project's preview target changed since you opened this page. Reload, then try again.",
  duplicate_command:
    "That request was already used for a different session. Try again to start a new one.",
  admission_refused:
    "The preview target refused this testing version's safety posture, so no session was started.",
  candidate_unusable:
    "This testing version is no longer available or has expired. Freeze a new one from the Preview dialog.",
  language_unavailable:
    "This preview target does not offer this language for a conversation. Choose another language.",
  candidate_validator_mismatch:
    "This testing version was validated by another engine build and must be rebuilt before it can run.",
  preview_target:
    "This project's preview target is not available for previews right now.",
  denied: "Your account cannot start preview sessions here.",
  unknown_dispatch:
    "The service did not confirm whether the session started. Try again: the same request cannot start a second one.",
  error: "The session could not be started.",
} as const satisfies Record<SimRunFailure["kind"], string>;

const PATIENT_GONE =
  "The preview target no longer offers that synthetic patient. Choose again.";

const CHAT_FAILURE =
  "The Liv patient chat could not be opened. Check that the testing version and a patient in this language are available, then try again.";

/**
 * Start a manual session on the testing version chosen in the top bar: the
 * clinician plays the patient from the Lab ("Start manual session"), or opens
 * the real Liv patient chat in a new tab for voice ("Open in Liv chat"). Both
 * select the new run so it can be watched live.
 *
 * One idempotency key per attempt, as the sim form: a changed form mints a new
 * one, a retry after a lost answer reuses it and cannot start a second session.
 * The chat link is the single-use credential: opened, never rendered or kept.
 */
export function LabNewManualForm({
  projectId,
  candidateId,
  onStarted,
  chatSlot,
}: {
  projectId: string;
  candidateId: string | null;
  onStarted: (previewRunRef: string) => void;
  /** The settings bar's More menu; standalone, the form shows a More of its own. */
  chatSlot?: HTMLElement | null;
}) {
  const create = useCapability("preview.create");
  const overview = useProjectOverviewQuery(projectId);
  const candidates = usePreviewCandidatesQuery(projectId);
  const patients = usePreviewPatientsQuery(projectId);
  const manual = useCreatePreviewManualSessionMutation(projectId);
  const chat = useCreatePreviewSessionMutation(projectId);

  const [language, setLanguage] = useState<SimLanguage>("he");
  const [patientRef, setPatientRef] = useState("");
  // After a language change that kept no patient the tester chooses; the first of the list is not a default then.
  const [needsChoice, setNeedsChoice] = useState(false);
  const [labFlags, setLabFlags] = useState<LabFlags>(defaultLabFlags);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const idempotencyKey = useRef<string>(crypto.randomUUID());

  const candidate =
    candidates.data?.items.find((item) => item.candidateId === candidateId) ??
    null;
  const revision = overview.data?.project.previewTargetRevision;
  const offered =
    patients.data?.items.filter((item) => item.language === language) ?? [];
  const patient =
    offered.find((item) => item.ref === patientRef) ??
    (needsChoice ? undefined : offered[0]);
  const mustChoose =
    patient === undefined && offered.length > 0 && !patients.isPending;
  const failure = manual.isError ? classifySimRunFailure(manual.error) : null;
  const patientGone =
    manual.error instanceof ApiError &&
    manual.error.status === 422 &&
    manual.error.envelope["field"] === "patientRef";

  const blocker = !create.allowed
    ? `${create.reason} Starting a session needs the "preview.create" permission.`
    : candidate === null
      ? candidates.isPending
        ? "Loading the testing version…"
        : "Choose a testing version in the top bar. Freeze one from the Preview dialog if there is none."
      : candidate.validity?.state === "expired"
        ? "This testing version has expired. Choose another one in the top bar."
        : revision === undefined
          ? "Loading the project…"
          : patients.isPending
            ? "Loading the synthetic patients…"
            : mustChoose
              ? "Choose the synthetic patient."
              : null;
  const chatBlocker =
    blocker ??
    (patient === undefined
      ? patients.isPending
        ? "Loading the synthetic patients…"
        : "The preview target offers no synthetic patient in this language."
      : null);

  function changed() {
    idempotencyKey.current = crypto.randomUUID();
    manual.reset();
    chat.reset();
  }

  function start(opened: (previewRunRef: string) => void = onStarted) {
    if (blocker !== null || candidate === null || revision === undefined) {
      return;
    }
    manual.mutate(
      {
        candidateId: candidate.candidateId,
        language,
        // Without the list the service binds its first patient in the language.
        ...(patient ? { patientRef: patient.ref } : {}),
        labFlags,
        expectedPreviewTargetRevision: revision,
        idempotencyKey: idempotencyKey.current,
      },
      {
        onSuccess(created) {
          idempotencyKey.current = crypto.randomUUID();
          if (patient) {
            rememberRun(created.previewRunRef, { patientRef: patient.ref });
          }
          opened(created.previewRunRef);
        },
      },
    );
  }

  function openChat() {
    if (
      chatBlocker !== null ||
      candidate === null ||
      revision === undefined ||
      patient === undefined
    ) {
      return;
    }
    chat.mutate(
      {
        candidateId: candidate.candidateId,
        patientRef: patient.ref,
        labFlags,
        expectedPreviewTargetRevision: revision,
      },
      {
        onSuccess(created) {
          // The URL is the credential: opened for the clinician, never shown.
          window.open(created.previewUrl, "_blank", "noopener,noreferrer");
          rememberRun(created.previewRunRef, { patientRef: patient.ref });
          onStarted(created.previewRunRef);
        },
      },
    );
  }

  const chatFailure = chat.isError ? classifySessionFailure(chat.error) : null;

  const chatAction = (
    <div className="space-y-2">
      <Button
        type="button"
        variant="outline"
        disabled={chatBlocker !== null || chat.isPending}
        title={chatBlocker ?? undefined}
        onClick={openChat}
      >
        {chat.isPending ? "Opening…" : "Open in Liv chat"}
      </Button>
      <p className="text-xs text-muted-foreground">
        Starts the real patient chat in a new tab (for voice). The
        conversation appears in History and can be watched live.
      </p>
      <LabImportConversation
        projectId={projectId}
        blocker={blocker}
        start={start}
        onStarted={onStarted}
        onStatus={setImportStatus}
      />
    </div>
  );

  return (
    <form
      className="contents"
      onSubmit={(event) => {
        event.preventDefault();
        start();
      }}
    >
      <LabBarField label="Language">
        {({ inputId }) => (
          <Select
            id={inputId}
            className="w-28"
            value={language}
            onChange={(event) => {
              const next = event.target.value as SimLanguage;
              const kept = patientForLanguage(
                patients.data?.items ?? [],
                patient,
                next,
              );
              setLanguage(next);
              setPatientRef(kept?.ref ?? "");
              setNeedsChoice(kept === undefined);
              changed();
            }}
          >
            {SIM_LANGUAGES.map((code) => (
              <option key={code} value={code}>
                {SIM_LANGUAGE_LABELS[code]}
              </option>
            ))}
          </Select>
        )}
      </LabBarField>
      <LabPatientField
        patients={offered}
        value={patient?.ref ?? ""}
        placeholder={needsChoice ? "Choose a patient" : undefined}
        onChange={(ref) => {
          setPatientRef(ref);
          changed();
        }}
      />
      <LabRunOptions
        value={labFlags}
        onChange={(next) => {
          setLabFlags(next);
          changed();
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="submit"
          disabled={blocker !== null || manual.isPending}
          title={blocker ?? undefined}
        >
          {manual.isPending
            ? "Starting…"
            : failure?.kind === "unknown_dispatch"
              ? "Retry session"
              : "New conversation"}
        </Button>
        {chatSlot === undefined ? <LabMoreMenu>{chatAction}</LabMoreMenu> : null}
        {chatSlot ? createPortal(chatAction, chatSlot) : null}
      </div>
      {blocker !== null ? (
        <p className="order-last basis-full text-xs text-muted-foreground">{blocker}</p>
      ) : null}
      {importStatus !== null ? (
        <p
          role="status"
          className="order-last basis-full text-xs text-muted-foreground"
        >
          {importStatus}
        </p>
      ) : null}
      {failure !== null ? (
        <p
          role="alert"
          className="order-last basis-full rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
        >
          {patientGone ? PATIENT_GONE : FAILURE_MESSAGES[failure.kind]}
        </p>
      ) : null}
      {chatFailure !== null ? (
        <p
          role="alert"
          className="order-last basis-full rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
        >
          {CHAT_FAILURE}
        </p>
      ) : null}
    </form>
  );
}
